// ── Fullscreen "cover" background helper. ──
// The inherited title screen painted its background at a fixed 0.58 scale, so at the responsive
// RESIZE viewport it never covered the canvas (grey gutters on large displays). This helper sizes
// any image to *cover* the whole viewport (object-fit: cover) and re-fits it on every resize, then
// dims it with a gradient-ish overlay so foreground UI reads cleanly. Returns a destroy fn.

import Phaser from '../lib/phaser.js';
import { TITLE_ASSET_KEYS } from '../assets/asset-keys.js';

/**
 * Add a fullscreen, cover-fitted background image plus a dark overlay to a scene.
 * @param {Phaser.Scene} scene
 * @param {string} [textureKey] defaults to the title painting
 * @returns {() => void} cleanup function (removes the resize listener)
 */
export function addFullscreenBackground(scene, textureKey = TITLE_ASSET_KEYS.BACKGROUND) {
  const hasTexture = scene.textures.exists(textureKey);
  const bg = hasTexture
    ? scene.add.image(0, 0, textureKey).setOrigin(0.5)
    : scene.add.rectangle(0, 0, 10, 10, 0x0a0a12, 1).setOrigin(0.5);

  // dark premium overlay so any background reads as a subtle backdrop, not noise.
  const overlay = scene.add.rectangle(0, 0, 10, 10, 0x05060d, 0.72).setOrigin(0.5);

  const fit = () => {
    const { width, height } = scene.scale;
    const cx = width / 2;
    const cy = height / 2;
    bg.setPosition(cx, cy);
    overlay.setPosition(cx, cy).setSize(width, height);
    if (hasTexture) {
      const src = scene.textures.get(textureKey).getSourceImage();
      const sw = src.width || width;
      const sh = src.height || height;
      // cover: scale up to the larger ratio so the image fully fills the viewport
      const scale = Math.max(width / sw, height / sh);
      bg.setScale(scale);
    } else {
      bg.setSize(width, height);
    }
  };

  fit();
  scene.scale.on(Phaser.Scale.Events.RESIZE, fit);
  const cleanup = () => scene.scale.off(Phaser.Scale.Events.RESIZE, fit);
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, cleanup);
  return cleanup;
}
